import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MoveHorizontal, Play, X } from "lucide-react";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";
import CompareSlider from "@/components/CompareSlider";
import { goToAnchor } from "@/lib/scroll";
import { IMG, STEPS, HERO_VIDEO_URL } from "@/data/site";

export default function ServicePage({ page }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const panoRef = useRef(null);

  useEffect(() => {
    document.title = page.metaTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", page.metaDesc);
    return () => {
      document.title = "VCONTRACTZ — Real Estate Photo Editing & Visual Post-Production Studio";
    };
  }, [page]);

  const contact = () => goToAnchor("#contact", navigate, location.pathname);

  return (
    <div data-testid={`service-page-${page.slug}`}>
      <section className="relative h-[72vh] min-h-[540px] overflow-hidden">
        {page.video ? (
          <video
            poster={IMG.hero}
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
            className="absolute inset-0 w-full h-full object-cover"
            aria-label="Cinematic real estate walkthrough video"
          >
            <source src="/assets/hero-video.webm" type="video/webm" />
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
        ) : (
          <img src={page.heroImg} alt={page.heroAlt} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/30 to-ink" />

        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
          <FadeUp>
            <button
              data-testid="service-back-link"
              onClick={() => goToAnchor("#services", navigate, location.pathname)}
              className="mb-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-ivory/60 hover:text-gold transition-colors duration-300"
            >
              <ArrowLeft size={14} />
              All Services
            </button>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Overline>{page.overline}</Overline>
          </FadeUp>
          <h1 className="mt-6 font-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tight">
            <MaskedLines lines={page.title} />
          </h1>
          <FadeUp delay={0.3} className="mt-6 max-w-xl">
            <p className="text-sm md:text-base text-ivory/80 font-light leading-relaxed">{page.intro}</p>
          </FadeUp>
          <FadeUp delay={0.45} className="mt-8 flex flex-wrap gap-4">
            <button data-testid="service-start-project-btn" onClick={contact} className="btn-primary">
              Start a Project
              <ArrowUpRight size={14} />
            </button>
            <button
              data-testid="service-before-after-btn"
              onClick={() => goToAnchor("#before-after", navigate, location.pathname)}
              className="btn-ghost"
            >
              View Before &amp; After
            </button>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 md:py-32 border-b hairline">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <Overline>What&rsquo;s Included</Overline>
            </FadeUp>
            <FadeUp delay={0.15} className="mt-8 space-y-5 text-sm md:text-base text-ivory/70 font-light leading-relaxed">
              {page.approach.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </FadeUp>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-ivory/10 border hairline self-start">
            {page.includes.map((it, i) => (
              <FadeUp key={it} delay={0.04 * i} className="bg-ink">
                <div className="px-6 py-6 flex items-center gap-4">
                  <span className="w-5 h-px bg-gold shrink-0" aria-hidden="true" />
                  <span className="text-[11px] uppercase tracking-[0.25em] text-ivory/80">{it}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {page.sliders && (
        <section className="py-24 md:py-32 border-b hairline">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <FadeUp>
              <Overline>Real Client Work</Overline>
            </FadeUp>
            <h2 className="mt-8 mb-14 font-display font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
              <MaskedLines lines={["SEE THE", "DIFFERENCE."]} />
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.sliders.map((s, i) => (
                <FadeUp key={s.title} delay={0.1 * i}>
                  <h3 className="font-display text-xl md:text-2xl font-light mb-4">{s.title}</h3>
                  <CompareSlider testId={s.testId} before={s.before} after={s.after} ratio="aspect-[3/2]" />
                  <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-muted">{s.caption}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.pano && (
        <section className="py-24 md:py-32 border-b hairline">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 mb-12">
            <FadeUp>
              <Overline>Immersive Output</Overline>
            </FadeUp>
            <h2 className="mt-8 font-display font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
              <MaskedLines lines={["EXPERIENCE", "THE SPACE."]} />
            </h2>
          </div>
          <FadeUp delay={0.1}>
            <div
              ref={panoRef}
              data-testid="service-panorama-drag"
              data-cursor="DRAG"
              className="relative overflow-hidden h-[55vh] border-y hairline"
            >
              <motion.div
                drag="x"
                dragConstraints={panoRef}
                dragElastic={0.06}
                className="h-full w-[190%] md:w-[160%] cursor-grab active:cursor-grabbing"
              >
                <img
                  src={IMG.panoImage}
                  alt="Seamless luxury property panorama — drag to explore"
                  className="w-full h-full object-cover pointer-events-none"
                  draggable={false}
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-ivory/70 pointer-events-none">
                <MoveHorizontal size={14} className="text-gold" />
                Drag to explore
              </div>
            </div>
          </FadeUp>
        </section>
      )}

      {page.video && (
        <section className="relative py-28 md:py-36 border-b hairline overflow-hidden">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col items-start">
            <FadeUp>
              <Overline>Showreel</Overline>
            </FadeUp>
            <h2 className="mt-8 font-display font-light text-4xl md:text-6xl leading-[0.98] tracking-tight max-w-3xl">
              <MaskedLines lines={["FROM RAW FOOTAGE", "TO REAL ESTATE CINEMA."]} />
            </h2>
            <FadeUp delay={0.3} className="mt-10">
              <button
                data-testid="service-video-play-btn"
                data-cursor="PLAY"
                onClick={() => setOpen(true)}
                className="group flex items-center gap-4"
                aria-label="Play showreel"
              >
                <span className="w-16 h-16 rounded-full border border-gold/60 flex items-center justify-center text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-ink">
                  <Play size={18} className="ml-0.5" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-ivory/80">Watch Showreel</span>
              </button>
            </FadeUp>
          </div>
          <AnimatePresence>
            {open && (
              <motion.div
                data-testid="service-video-lightbox"
                className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                onClick={() => setOpen(false)}
              >
                <button
                  data-testid="service-video-lightbox-close"
                  className="absolute top-6 right-6 text-ivory/70 hover:text-gold transition-colors p-2"
                  aria-label="Close video"
                >
                  <X size={28} />
                </button>
                <motion.video
                  controls
                  autoPlay
                  className="w-full max-w-5xl border hairline"
                  initial={{ scale: 0.94, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <source src="/assets/showreel.webm" type="video/webm" />
                </motion.video>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      )}

      <section className="py-24 md:py-32 border-b hairline">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <FadeUp>
            <Overline>The Workflow</Overline>
          </FadeUp>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {STEPS.map((s, i) => (
              <FadeUp key={s.num} delay={0.1 * i}>
                <div className="border-l hairline pl-6">
                  <span className="font-display text-4xl font-light text-gold/50">{s.num}</span>
                  <h3 className="mt-4 text-xs uppercase tracking-[0.35em] text-ivory">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted font-light leading-relaxed">{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.0] tracking-tight">
            <MaskedLines lines={["SEND A", "TEST IMAGE."]} />
          </h2>
          <FadeUp delay={0.25} className="mt-6 max-w-lg mx-auto">
            <p className="text-sm md:text-base text-ivory/70 font-light leading-relaxed">
              Evaluate our quality and communication on a real frame from your own shoot — before any commitment.
            </p>
          </FadeUp>
          <FadeUp delay={0.4} className="mt-10">
            <button data-testid="service-test-image-btn" data-cursor="START" onClick={contact} className="btn-primary">
              Start a Project
              <ArrowUpRight size={14} />
            </button>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
